import React from 'react'

import {makeStyles} from '@material-ui/core/styles';
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";

import Seo from "../../components/seo/Seo";
import {LogoContrast} from "../../components/BrandItems";

import heroDesktop from "../../../images/maintenance-hero-21x9.jpg";
import heroMobile from "../../../images/maintenance-hero-4x5.jpg";

/**
 * Full-screen page shown when MAINTENANCE_MODE is true (src/js/constants/index.js).
 * Intentionally self-contained: no redux, no API calls (the backend is down).
 * Hero image: the XC60 hero from volvocars.com (21:9 from the md breakpoint up,
 * 4:5 crop below it), covering the whole viewport; logo, message and copyright
 * are overlaid on it. Desktop: text bottom-left. Mobile/tablet: text at the top,
 * above the car.
 */
function MaintenancePage() {
    const classes = useStyles();

    return (
        <React.Fragment>
            <Seo data={{
                meta_title: "Mentenanță | Oferte Volvo",
                meta_og_title: "Mentenanță | Oferte Volvo",
                meta_description: "Site-ul OferteVolvo.ro este momentan în mentenanță. Revenim în cel mai scurt timp.",
                meta_og_description: "Site-ul OferteVolvo.ro este momentan în mentenanță. Revenim în cel mai scurt timp.",
            }}/>
            <main className={classes.hero}>
                <div className={classes.heroOverlay}/>
                <div className={classes.heroInner}>
                    <header className={classes.header}>
                        <Container maxWidth={"lg"}>
                            <div className={classes.logo} aria-label="Volvo">
                                <LogoContrast/>
                            </div>
                        </Container>
                    </header>
                    <Container maxWidth={"lg"} className={classes.content}>
                        <Typography
                            variant="h2"
                            component="h1"
                            className={classes.title}
                        >
                            Site-ul este momentan în mentenanță
                        </Typography>
                        <Typography variant={"body1"} component={"p"} className={classes.text}>
                            Lucrăm la îmbunătățirea platformei OferteVolvo.ro și vom reveni în cel mai scurt timp.
                        </Typography>
                        <Typography variant={"body1"} component={"p"} className={classes.text}>
                            Vă mulțumim pentru înțelegere!
                        </Typography>
                    </Container>
                    <footer className={classes.footer}>
                        <Container maxWidth={"lg"}>
                            <Typography component={"p"} className={classes.caption}>
                                Drepturi de autor ©{new Date().getFullYear()} Volvo Car Corporation (afiliaţii sau
                                licenţiatorii acestora).
                            </Typography>
                        </Container>
                    </footer>
                </div>
            </main>
        </React.Fragment>
    )
}

const useStyles = makeStyles((theme) => ({
    hero: {
        position: "relative",
        overflow: "hidden",
        minHeight: "100vh",
        display: "flex",
        backgroundColor: "#d9d2c8",
        backgroundImage: `url(${heroDesktop})`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        [theme.breakpoints.down('sm')]: {
            backgroundImage: `url(${heroMobile})`,
        },
    },
    heroOverlay: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        // desktop: darken the top edge (logo) and the left side (text)
        background: [
            "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 22%)",
            "linear-gradient(90deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0) 70%)",
        ].join(", "),
        // mobile/tablet: text sits at the top, so darken the top; a touch at the bottom for the caption
        [theme.breakpoints.down('sm')]: {
            background: [
                "linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.50) 32%, rgba(0,0,0,0) 60%)",
                "linear-gradient(0deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 18%)",
            ].join(", "),
        },
    },
    heroInner: {
        position: "relative",
        zIndex: 1,
        flex: "1 1 auto",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
    },
    header: {
        flex: "0 0 auto",
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    logo: {
        display: "flex",
        alignItems: "center",
        minHeight: 32,
    },
    content: {
        flex: "1 1 auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(8),
        [theme.breakpoints.down('sm')]: {
            justifyContent: "flex-start",
            paddingTop: theme.spacing(3),
            paddingBottom: theme.spacing(4),
        },
    },
    footer: {
        flex: "0 0 auto",
        paddingBottom: theme.spacing(3),
    },
    title: {
        color: "#fff",
        fontWeight: 300,
        fontSize: theme.typography.pxToRem(48),
        lineHeight: 1.15,
        maxWidth: 640,
        marginBottom: theme.spacing(3),
        textShadow: "0 1px 12px rgba(0,0,0,0.25)",
        [theme.breakpoints.down('md')]: {
            fontSize: theme.typography.pxToRem(40),
            maxWidth: 480,
        },
        [theme.breakpoints.down('xs')]: {
            fontSize: theme.typography.pxToRem(32),
        },
    },
    text: {
        color: "#fff",
        fontWeight: 300,
        fontSize: theme.typography.pxToRem(17),
        lineHeight: theme.typography.pxToRem(26),
        maxWidth: 560,
        marginBottom: theme.spacing(1),
        textShadow: "0 1px 8px rgba(0,0,0,0.25)",
        [theme.breakpoints.down('md')]: {
            maxWidth: 440,
        },
    },
    caption: {
        fontSize: 12,
        lineHeight: "18px",
        fontWeight: 300,
        letterSpacing: 0,
        color: "rgba(255,255,255,0.7)",
    },
}))

export default MaintenancePage;
